import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Decode JWT expiry without a library — payload is just base64
function getTokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null; // convert to ms
    } catch {
        return null;
    }
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('auth_token'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const refreshTimerRef = useRef(null);

    const scheduleRefresh = useCallback((currentToken) => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        const expiry = getTokenExpiry(currentToken);
        if (!expiry) return;
        // Refresh 30 minutes before expiry
        const delay = expiry - Date.now() - 30 * 60 * 1000;
        if (delay <= 0) return;
        refreshTimerRef.current = setTimeout(async () => {
            try {
                const response = await axios.post(`${API_URL}/auth/refresh`);
                const newToken = response.data.access_token;
                localStorage.setItem('auth_token', newToken);
                setToken(newToken);
                scheduleRefresh(newToken);
            } catch {
                // Token already expired or invalid — log out silently
                localStorage.removeItem('auth_token');
                setToken(null);
                setUser(null);
            }
        }, delay);
    }, []);

    // Set up axios interceptor for auth header
    useEffect(() => {
        const interceptor = axios.interceptors.request.use((config) => {
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        return () => axios.interceptors.request.eject(interceptor);
    }, [token]);

    // Check if user is authenticated on mount
    const checkAuth = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await axios.get(`${API_URL}/auth/me`);
            setUser(response.data);
            scheduleRefresh(token);
        } catch (err) {
            console.error('Auth check failed:', err);
            localStorage.removeItem('auth_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token, scheduleRefresh]);

    useEffect(() => {
        checkAuth();
        return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
    }, [checkAuth]);

    const login = async (email, password) => {
        setError(null);
        try {
            const response = await axios.post(`${API_URL}/auth/login`, {
                email,
                password
            });
            const { access_token, user: userData } = response.data;
            localStorage.setItem('auth_token', access_token);
            setToken(access_token);
            setUser(userData);
            return { success: true };
        } catch (err) {
            const message = err.response?.data?.detail || 'Login failed. Please try again.';
            setError(message);
            return { success: false, error: message };
        }
    };

    const register = async (name, email, password) => {
        setError(null);
        try {
            const response = await axios.post(`${API_URL}/auth/register`, {
                name,
                email,
                password
            });
            const { access_token, user: userData } = response.data;
            localStorage.setItem('auth_token', access_token);
            setToken(access_token);
            setUser(userData);
            return { success: true };
        } catch (err) {
            const message = err.response?.data?.detail || 'Registration failed. Please try again.';
            setError(message);
            return { success: false, error: message };
        }
    };

    const logout = () => {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        error,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        login,
        register,
        logout,
        clearError: () => setError(null)
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
